import { useState } from "react";

export interface Slice {
  label: string;
  value: number;
  color: string;
  display?: string; // overrides the formatted value in the legend
  sub?: string;
}

/** Donut with the prototype's hover behavior: hovered slice thickens, others dim,
 *  tooltip follows the cursor. Legend format: `818 (65%)`. */
export function Donut({ slices, size = 110 }: { slices: Slice[]; size?: number }) {
  const [hover, setHover] = useState<number | null>(null);
  const [tip, setTip] = useState<{ x: number; y: number; text: string } | null>(null);
  const total = slices.reduce((a, s) => a + s.value, 0);
  if (total <= 0) return <span style={{ fontSize: 11, color: "var(--t-dim)" }}>no data in scope</span>;

  const r = 44;
  const C = 2 * Math.PI * r;
  let acc = 0;

  return (
    <div className="donutbox">
      <svg width={size} height={size} viewBox="0 0 110 110" style={{ transform: "rotate(-90deg)" }}>
        {slices.map((s, i) => {
          if (s.value <= 0) return null;
          const len = (s.value / total) * C;
          const off = -acc;
          acc += len;
          const pct = Math.round((s.value / total) * 100);
          return (
            <circle
              key={i}
              cx="55"
              cy="55"
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={hover === i ? 17 : 14}
              strokeDasharray={`${len} ${C - len}`}
              strokeDashoffset={off}
              opacity={hover === null || hover === i ? 1 : 0.3}
              style={{ transition: "opacity .12s, stroke-width .12s" }}
              onMouseEnter={(e) => {
                setHover(i);
                setTip({
                  x: e.clientX,
                  y: e.clientY,
                  text: `${s.label} - ${s.display ?? s.value.toLocaleString("en-IN")} (${pct}%)`,
                });
              }}
              onMouseMove={(e) => setTip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : t))}
              onMouseLeave={() => {
                setHover(null);
                setTip(null);
              }}
            />
          );
        })}
      </svg>
      <div className="dl">
        {slices
          .filter((s) => s.value > 0)
          .map((s, i) => (
            <div className="dli" key={i}>
              <i style={{ background: s.color }} />
              {s.label}
              {s.sub && (
                <span style={{ fontSize: 10, color: "var(--t-dim)", marginLeft: 6 }}>{s.sub}</span>
              )}
              <b className="num">
                {s.display ?? s.value.toLocaleString("en-IN")}{" "}
                <span className="pct">({Math.round((s.value / total) * 100)}%)</span>
              </b>
            </div>
          ))}
      </div>
      {tip && (
        <div id="donut-tip" style={{ display: "block", left: tip.x + 14, top: tip.y + 14 }}>
          {tip.text}
        </div>
      )}
    </div>
  );
}
